import { forwardRef, useEffect, useId, useRef, useState, type ComponentType } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import {
  Bell,
  BellOff,
  CheckCheck,
  ClipboardList,
  CreditCard,
  Info,
  Megaphone,
  PackageX,
  ShoppingBag,
} from 'lucide-react';
import { formatDateTime, formatRelative, type NotificationDTO, type Paginated } from '@dezza/shared';
import { Button, IconButton, Skeleton, cn, durations, easings, springs, toast } from '@dezza/ui';
import { api, errorMessage } from '../lib/api';
import { useMe } from '../lib/auth';

export type NotificationsPageDTO = Paginated<NotificationDTO> & { unread: number };

/** Ícone, cor e rótulo de cada tipo de notificação (painel do sino e tela Notificações). */
export const NOTIFICATION_META: Record<
  NotificationDTO['type'],
  { icon: ComponentType<{ className?: string }>; tone: string; label: string }
> = {
  new_order: { icon: ShoppingBag, tone: 'bg-primary-soft text-primary-strong', label: 'Novo pedido' },
  order_update: { icon: ClipboardList, tone: 'bg-info-soft text-info', label: 'Pedido' },
  low_stock: { icon: PackageX, tone: 'bg-warning-soft text-warning', label: 'Estoque baixo' },
  subscription: { icon: CreditCard, tone: 'bg-gold-soft text-warning', label: 'Assinatura' },
  system: { icon: Info, tone: 'bg-surface-2 text-ink-2', label: 'Aviso' },
  announcement: { icon: Megaphone, tone: 'bg-info-soft text-info', label: 'Novidade' },
};

/** Abrir uma notificação: marca como lida (sem esperar) e segue o link. */
export function useOpenNotification() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  return (n: NotificationDTO) => {
    if (!n.readAt) {
      api
        .post(`/notifications/${n.id}/read`)
        .then(() =>
          Promise.all([
            qc.invalidateQueries({ queryKey: ['notifications'] }),
            qc.invalidateQueries({ queryKey: ['me'] }),
          ]),
        )
        .catch(() => undefined);
    }
    if (n.link) {
      if (/^https?:\/\//.test(n.link)) window.open(n.link, '_blank', 'noopener');
      else navigate(n.link);
    }
  };
}

export function useMarkAllRead() {
  const qc = useQueryClient();
  const [pending, setPending] = useState(false);
  const run = async () => {
    setPending(true);
    try {
      const r = await api.post<{ count: number }>('/notifications/read-all');
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['notifications'] }),
        qc.invalidateQueries({ queryKey: ['me'] }),
      ]);
      toast.success(r.count ? 'Tudo marcado como lido.' : 'Nada novo por aqui.');
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setPending(false);
    }
  };
  return { run, pending };
}

/** Linha de notificação (painel e tela cheia). */
export function NotificationRow({
  n,
  onOpen,
  compact,
}: {
  n: NotificationDTO;
  onOpen: (n: NotificationDTO) => void;
  compact?: boolean;
}) {
  const meta = NOTIFICATION_META[n.type] ?? NOTIFICATION_META.system;
  const Icon = meta.icon;
  const unread = !n.readAt;
  return (
    <button
      type="button"
      onClick={() => onOpen(n)}
      className={cn(
        'group relative flex w-full items-start gap-3 text-left outline-none transition-colors duration-100 hover:bg-ink/[0.03] focus-visible:bg-ink/[0.05]',
        compact ? 'px-4 py-3' : 'px-4 py-3.5',
      )}
    >
      <span
        className={cn(
          'flex shrink-0 items-center justify-center rounded-xl',
          compact ? 'size-9' : 'size-10',
          meta.tone,
        )}
      >
        <Icon className={compact ? 'size-[18px]' : 'size-5'} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-start gap-2">
          <span
            className={cn(
              'min-w-0 flex-1 text-[14.5px] leading-snug text-ink',
              unread ? 'font-semibold' : 'font-medium',
            )}
          >
            {n.title}
          </span>
          <time
            className="tabular shrink-0 pt-0.5 text-xs text-ink-3"
            dateTime={n.createdAt}
            title={formatDateTime(n.createdAt)}
          >
            {formatRelative(n.createdAt)}
          </time>
        </span>
        {n.body ? (
          <span
            className={cn(
              'mt-0.5 block text-[13.5px] leading-relaxed text-ink-2',
              compact ? 'line-clamp-2' : 'line-clamp-3',
            )}
          >
            {n.body}
          </span>
        ) : null}
        {compact ? null : <span className="mt-1 block text-xs font-medium text-ink-3">{meta.label}</span>}
      </span>
      {unread ? (
        <span className="mt-2 size-2 shrink-0 rounded-full bg-primary" role="img" aria-label="Não lida" />
      ) : null}
    </button>
  );
}

const NotificationPanel = forwardRef<HTMLDivElement, { id: string; onClose: () => void }>(
  function NotificationPanel({ id, onClose }, ref) {
    const reduce = useReducedMotion();
    const open = useOpenNotification();
    const markAll = useMarkAllRead();
    const headingRef = useRef<HTMLHeadingElement>(null);
    const q = useQuery({
      queryKey: ['notifications', 'latest'],
      queryFn: () => api.get<NotificationsPageDTO>('/notifications', { pageSize: 8 }),
      staleTime: 10_000,
    });
    const items = q.data?.items ?? [];
    const unread = q.data?.unread ?? 0;
    useEffect(() => headingRef.current?.focus(), []);

    return (
      <motion.div
        ref={ref}
        id={id}
        role="dialog"
        aria-modal="false"
        aria-labelledby={`${id}-title`}
        initial={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.97, y: -6 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={
          reduce
            ? { opacity: 0 }
            : {
                opacity: 0,
                scale: 0.98,
                y: -4,
                transition: { duration: durations.fast, ease: easings.standard },
              }
        }
        transition={reduce ? { duration: durations.fast } : springs.gentle}
        style={{ transformOrigin: 'top right' }}
        className="absolute right-0 top-full z-50 mt-2 flex max-h-[min(560px,calc(100dvh-5rem))] w-[392px] flex-col overflow-hidden rounded-2xl bg-elevated text-ink shadow-pop"
      >
        <div className="flex items-center gap-2 border-b border-line px-4 py-3">
          <h2
            ref={headingRef}
            id={`${id}-title`}
            tabIndex={-1}
            className="flex-1 text-[15px] font-semibold tracking-[-0.01em] outline-none"
          >
            Notificações
            {unread ? (
              <span className="tabular ml-2 rounded-full bg-primary-soft px-2 py-0.5 text-xs font-semibold text-primary-strong">
                {unread > 99 ? '99+' : unread}
              </span>
            ) : null}
          </h2>
          {unread ? (
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<CheckCheck />}
              loading={markAll.pending}
              onClick={() => void markAll.run()}
              className="-mr-2 h-8 px-2 text-[13px]"
            >
              Marcar como lidas
            </Button>
          ) : null}
        </div>
        <div className="scrollbar-thin min-h-0 flex-1 overflow-y-auto overscroll-contain">
          {q.isPending ? (
            <div className="space-y-4 p-4" role="status" aria-label="Carregando notificações">
              {[0, 1, 2].map((i) => (
                <div key={i} className="flex gap-3">
                  <Skeleton className="size-9 rounded-xl" />
                  <div className="flex-1 space-y-2 pt-0.5">
                    <Skeleton className="h-3.5 w-3/4" />
                    <Skeleton className="h-3 w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : q.isError ? (
            <p className="px-4 py-8 text-center text-sm text-ink-2">{errorMessage(q.error)}</p>
          ) : items.length ? (
            <ul className="divide-y divide-line">
              {items.map((n) => (
                <li key={n.id}>
                  <NotificationRow
                    n={n}
                    compact
                    onOpen={(item) => {
                      onClose();
                      open(item);
                    }}
                  />
                </li>
              ))}
            </ul>
          ) : (
            <div className="flex flex-col items-center px-6 py-10 text-center">
              <span className="mb-3 flex size-11 items-center justify-center rounded-2xl bg-surface-2 text-ink-3 ring-1 ring-inset ring-line">
                <BellOff className="size-5" aria-hidden="true" />
              </span>
              <p className="text-[15px] font-semibold text-ink">Nada por aqui ainda</p>
              <p className="mt-1 text-sm text-ink-2">
                Novos pedidos do catálogo, estoque baixo e avisos da assinatura aparecem aqui.
              </p>
            </div>
          )}
        </div>
        <Link
          to="/notificacoes"
          onClick={onClose}
          className="border-t border-line bg-surface-2/60 px-4 py-2.5 text-center text-[13px] font-semibold text-primary-strong outline-none transition-colors hover:bg-surface-2 focus-visible:bg-surface-2"
        >
          Ver todas as notificações
        </Link>
      </motion.div>
    );
  },
);

/** Sino do topo (desktop): painel suspenso com as últimas notificações. */
export function NotificationBell() {
  const me = useMe();
  const unread = me.unreadNotifications;
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const id = useId().replace(/:/g, '');

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (panelRef.current?.contains(t) || btnRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      btnRef.current?.focus();
    };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="relative">
      <IconButton
        ref={btnRef}
        aria-label={unread ? `Notificações, ${unread} não lidas` : 'Notificações'}
        title="Notificações"
        aria-expanded={open}
        aria-controls={open ? `notif-${id}` : undefined}
        aria-haspopup="dialog"
        badge={unread}
        onClick={() => setOpen((v) => !v)}
        className={open ? 'bg-ink/[0.06] text-ink' : undefined}
      >
        <Bell />
      </IconButton>
      <AnimatePresence>
        {open ? <NotificationPanel ref={panelRef} id={`notif-${id}`} onClose={() => setOpen(false)} /> : null}
      </AnimatePresence>
    </div>
  );
}

/** Sino do topo no celular: abre a tela de notificações. */
export function MobileBell() {
  const me = useMe();
  const navigate = useNavigate();
  const unread = me.unreadNotifications;
  return (
    <IconButton
      variant="on-dark"
      aria-label={unread ? `Notificações, ${unread} não lidas` : 'Notificações'}
      badge={unread}
      onClick={() => navigate('/notificacoes')}
    >
      <Bell />
    </IconButton>
  );
}
